import { DatePicker } from "@/components/ui/DatePicker";
import type { RecurrenceFreq, RecurrenceRule } from "../types";

interface Props {
  value: RecurrenceRule;
  onChange: (rule: RecurrenceRule) => void;
}

const FREQ_OPTIONS: { value: RecurrenceFreq; label: string }[] = [
  { value: "daily", label: "Codziennie" },
  { value: "weekly", label: "Co tydzień" },
  { value: "biweekly", label: "Co 2 tygodnie" },
  { value: "monthly", label: "Co miesiąc" },
];

const WEEKDAYS = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"];

const INPUT =
  "rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)]";

export function RecurrenceEditor({ value, onChange }: Props) {
  const needsWeekday = value.freq === "weekly" || value.freq === "biweekly";
  const needsMonthday = value.freq === "monthly";

  return (
    <div className="space-y-3 rounded-md border border-[var(--color-border)] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[var(--color-text-secondary)]">Powtarzaj:</span>
        {FREQ_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ ...value, freq: opt.value })}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
              value.freq === opt.value
                ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {needsWeekday && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-sm text-[var(--color-text-secondary)]">Dzień:</span>
          {WEEKDAYS.map((label, idx) => (
            <button
              key={label}
              type="button"
              onClick={() => onChange({ ...value, weekday: idx })}
              className={`h-8 w-8 rounded-full border text-xs transition-colors ${
                value.weekday === idx
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {needsMonthday && (
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          Dzień miesiąca:
          <input
            type="number"
            min={1}
            max={31}
            value={value.monthday ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                monthday: e.target.value ? Number(e.target.value) : null,
              })
            }
            className={`${INPUT} w-20`}
          />
          <span className="text-xs text-[var(--color-text-muted)]">
            (31 → ostatni dzień krótszych miesięcy)
          </span>
        </label>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          Godzina:
          <input
            type="time"
            value={value.time}
            onChange={(e) => onChange({ ...value, time: e.target.value })}
            className={INPUT}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          Do dnia (opcjonalnie):
          <DatePicker
            value={value.until ?? ""}
            onChange={(v) => onChange({ ...value, until: v || null })}
          />
        </label>
      </div>
    </div>
  );
}
